import React from "react";
import cx from "classnames";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import SearchLine from "../IconV2/react/SearchLine";
import { colors } from "../Theme";
import ClearButton from "./ClearButton";
import type { Props as InputProps } from "./InputV2";
import Input from "./InputV2";
import { css } from "@linaria/core";

const hasNoNativeClearSupport = CSS.supports("-moz-appearance: none");

/**
 *
 * @param Props See {@link ./InputV2.tsx}
 * @param forwardedRef HTMLInputElement
 * @returns JSX.Element
 */
function SearchInput(
  {
    value,
    onChange,
    showIcon = true,
    autoFocus = false,
    slim = false,
    ...props
  }: InputProps & { showIcon?: boolean; autoFocus?: boolean; slim?: boolean },
  forwardedRef: React.Ref<HTMLInputElement>,
) {
  const ref = useEnsuredForwardedRef(forwardedRef);

  React.useEffect(() => {
    /**
     * Prefer a window.requestAnimationFrame() in case where the DOM element is not yet rendered
     */
    if (autoFocus && ref.current) window.requestAnimationFrame(() => ref.current?.focus());
  }, [ref, autoFocus]);

  return (
    <Input
      {...props}
      ref={ref}
      className={slim ? cx(slimSearchInput, props.className) : props.className}
      type="search"
      value={value}
      onChange={onChange}
      beforeInputSlot={showIcon ? <SearchLine size="s" color={colors.titanium300} /> : undefined}
      afterInputSlot={hasNoNativeClearSupport ? <ClearButton target={ref} /> : undefined}
    />
  );
}

export default React.memo(React.forwardRef(SearchInput));

const slimSearchInput = css`
  input {
    padding-top: 5px;
    padding-bottom: 5px;
  }
`;
